export const AFTER_FIRST_PAINT_FALLBACK_MS = 100;

interface FirstPaintScheduler {
  requestAnimationFrame(callback: FrameRequestCallback): number;
  setTimeout(handler: TimerHandler, timeout?: number): number;
  clearTimeout(handle: number): void;
}

/**
 * Runs `task` once the browser has painted the current document.
 *
 * Hydration is one long main-thread task. Started straight from the entry
 * module, it can run before Chrome's first frame, so the server-rendered
 * headline only paints after all of React has booted (the 10 s mobile LCP
 * render delay in #1301). A rAF callback runs just before a frame is painted;
 * a zero timeout queued from it runs just after, so the server HTML is on
 * screen before hydration starts.
 *
 * Hidden tabs do not run rAF, so they start right away, and the fallback
 * timeout keeps a stalled frame loop from holding hydration back.
 */
export function runAfterFirstPaint(
  task: () => void,
  scheduler: FirstPaintScheduler = window,
  isHidden: () => boolean = () => typeof document !== 'undefined' && document.visibilityState === 'hidden'
): void {
  let ran = false;
  let fallbackId: number | null = null;

  const run = () => {
    if (ran) return;
    ran = true;
    if (fallbackId !== null) {
      scheduler.clearTimeout(fallbackId);
      fallbackId = null;
    }
    task();
  };

  if (isHidden()) {
    run();
    return;
  }

  scheduler.requestAnimationFrame(() => {
    scheduler.setTimeout(run, 0);
  });
  fallbackId = scheduler.setTimeout(run, AFTER_FIRST_PAINT_FALLBACK_MS);
}
